import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { ShieldCheck, Users, ClipboardList, CheckCircle2, Star, Stethoscope, Home as HomeIcon, Clock } from 'lucide-react';
import Button from '../Button';
import Kontakt from '../components/Kontakt';
import StandorteSection from '../components/StandorteSection';
import Preisrechner from '../components/Preisrechner';
import { services, getServiceBySlug } from '../data/services';
import logo from '../logo.svg';
import {
  Section,
  Container,
  Badge,
  MotionCard,
  CardHeader,
  CardTitle,
  CardContent,
  SectionDivider,
  AnimatedSection,
  WhatsAppButton,
  AnimatedGradient,
  FloatingBlobs,
  WaterOverlay,
} from '../shared/ui';
import { imageProps, socialImagePath, ImageName, SIZES } from '../shared/media';

// --- Hero ---
const HeroBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 5rem 0 3rem 0;
  @media (max-width: 600px) {
    padding: 3rem 0 2rem 0;
  }
`;
const HeroLogo = styled.img`
  height: 240px;
  width: auto;
  display: block;
  margin-bottom: 2rem;
  filter: drop-shadow(0 12px 32px rgba(0, 52, 196, 0.18));

  @media (max-width: 600px) {
    height: 160px;
    margin-bottom: 1.5rem;
  }
`;
const HeroRating = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.55rem;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 0.4rem 1.05rem;
  box-shadow: var(--shadow-sm);
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  color: var(--ink-muted);
`;
const HeroRatingScore = styled.strong`
  color: var(--ink);
  font-weight: 700;
`;
const HeroRatingDivider = styled.span`
  color: var(--border);
`;
const HeroTitle = styled(motion.h1)`
  font-size: clamp(2rem, 5vw, 3.25rem);
  font-weight: 800;
  letter-spacing: -1.5px;
  line-height: 1.1;
  color: var(--ink);
  margin-bottom: 1.25rem;
  max-width: 820px;
`;
const HeroHighlight = styled.span`
  color: var(--brand);
`;
const HeroText = styled.p`
  margin-top: 0;
  max-width: 620px;
  font-size: 1.18rem;
  line-height: 1.65;
  color: var(--ink-muted);
`;
const HeroButtonRow = styled.div`
  margin-top: 2.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  @media (min-width: 600px) {
    flex-direction: row;
    justify-content: center;
  }
`;
const HeroBadgeRow = styled.div`
  margin-top: 2.5rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.55rem;
`;
const HeroBadgePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(0, 52, 196, 0.06);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 0.4em 0.9em;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--brand);
`;
const HeroPanel = styled.div`
  position: relative;
  z-index: 1;
  background: var(--bg, #ffffff);
  border: 1px solid var(--border);
  border-radius: 28px;
  box-shadow: 0 30px 80px rgba(0, 52, 196, 0.12);
  max-width: 860px;
  margin: 0 auto;
  padding: 1.5rem 2.5rem;
  @media (max-width: 700px) {
    padding: 1rem 1.25rem;
    border-radius: 20px;
  }
`;
const Hero = () => (
  <Section style={{ overflow: 'hidden', position: 'relative' }}>
    <AnimatedGradient />
    <FloatingBlobs />
    <WaterOverlay name="wasser-hintergrund" />
    <Container style={{ position: 'relative', zIndex: 1 }}>
      <HeroPanel>
      <HeroBox>
        <HeroLogo src={logo} alt="AP Clean Services" />
        <Badge>Ihr Spezialist für Praxisreinigung</Badge>
        <HeroRating>
          <Stars rating={5} />
          <HeroRatingScore>5,0</HeroRatingScore>
          <HeroRatingDivider>·</HeroRatingDivider>
          <GoogleG size={16} />
          <span>Google-Rezensionen</span>
        </HeroRating>
        <HeroTitle initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          Spezialisierte <HeroHighlight>Praxisreinigung</HeroHighlight> in Bielefeld, Gütersloh und Umgebung
        </HeroTitle>
        <HeroText>Die Reinigungskraft fällt aus, ständig steht jemand Neues in den Behandlungsräumen, und vor der nächsten Begehung fehlt der Nachweis. Genau hier setzen wir an: festes, eingewiesenes Team statt wechselnder Subunternehmer, Reinigung nach Praxisschluss, Vertretung bei Ausfall und ab dem Paket Praxis Plus auf Wunsch Protokolle für Ihr Qualitätsmanagement.</HeroText>
        <HeroButtonRow>
          <Link to="/praxisreinigung"><Button size="lg">Praxisreinigung entdecken</Button></Link>
          <Link to="/kontakt"><Button size="lg" variant="outline">Kostenlose Besichtigung anfragen</Button></Link>
        </HeroButtonRow>
        <HeroBadgeRow>
          <HeroBadgePill><HomeIcon style={{ height: 15, width: 15 }} />Familienbetrieb</HeroBadgePill>
          <HeroBadgePill><Users style={{ height: 15, width: 15 }} />Festes Team, keine Subunternehmer</HeroBadgePill>
          <HeroBadgePill><ClipboardList style={{ height: 15, width: 15 }} />Außerhalb der Öffnungszeiten</HeroBadgePill>
          <HeroBadgePill><ShieldCheck style={{ height: 15, width: 15 }} />3 Mio € Betriebshaftpflicht</HeroBadgePill>
          <HeroBadgePill><Clock style={{ height: 15, width: 15 }} />Rückmeldung in 24 h</HeroBadgePill>
        </HeroBadgeRow>
      </HeroBox>
      </HeroPanel>
    </Container>
  </Section>
);

// --- Problem ---
const ProblemTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
  color: var(--brand);
  margin-bottom: 1.5rem;
`;
const ProblemText = styled.p`
  max-width: 760px;
  margin: 0 auto 1.2rem auto;
  text-align: center;
  font-size: 1.12rem;
  line-height: 1.7;
  color: var(--ink-muted);
`;
const Problem = () => (
  <Section id="warum">
    <Container>
      <ProblemTitle>Das Problem ist selten der Schmutz</ProblemTitle>
      <ProblemText>
        Es ist der Anruf um sieben, dass heute niemand kommt. Es sind die wechselnden Gesichter, die abends allein durch Ihre Behandlungsräume gehen. Es ist das Nachfassen, weil der Sanitärbereich schon wieder übersehen wurde. Und es ist die Frage vor der nächsten Begehung, wie Sie eigentlich belegen, was wann gereinigt wurde.
      </ProblemText>
      <ProblemText>
        Reinigung ist keine Leistung, die man einkauft und abhakt — sie ist ein Risiko, das man aus dem Kopf bekommen möchte. Genau dafür arbeiten wir mit festen, eingewiesenen Teams statt mit Subunternehmern, mit organisierter Vertretung bei Ausfall und, in Praxen ab dem Paket Praxis Plus, auf Wunsch mit Reinigungsprotokollen für Ihr Qualitätsmanagement.
      </ProblemText>
      <ProblemText>
        Unser Schwerpunkt ist die Praxisreinigung. Darüber hinaus reinigen wir Büros, Treppenhäuser, Glasflächen und mehr.
      </ProblemText>
    </Container>
  </Section>
);

// --- Leistungen (Übersicht mit Links) ---
const LeistungenTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
  color: var(--ink);
  margin-bottom: 0.5rem;
`;
const LeistungenDesc = styled.p`
  text-align: center;
  color: var(--ink-muted);
  margin-bottom: 2rem;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
`;
const LeistungenGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;
  @media (min-width: 600px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (min-width: 900px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;
const LeistungenIconTitle = styled(CardTitle)`
  gap: 0.5rem;
  align-items: center;
  display: flex;
`;
const LeistungenContent = styled(CardContent)`
  color: var(--ink-muted);
`;
const LeistungSubList = styled.ul`
  list-style: none;
  margin: 0.75rem 0 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const LeistungSubItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  color: var(--ink-muted);
  font-size: 0.98rem;
`;
const LeistungLink = styled(Link)`
  display: inline-block;
  margin-top: 1rem;
  color: var(--brand);
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
const CardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
`;
const LeistungCard = styled(MotionCard)`
  overflow: hidden;
`;
const LeistungImageWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: linear-gradient(135deg, var(--brand) 0%, var(--brand-600) 55%, var(--brand-light) 100%);
`;
const LeistungImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  ${LeistungCard}:hover & {
    transform: scale(1.08);
  }
`;
const LeistungImageFallback: React.FC<{ name: ImageName; alt: string }> = ({ name, alt }) => {
  const [failed, setFailed] = React.useState(false);
  if (failed) return null;
  return <LeistungImage {...imageProps(name, SIZES.card)} alt={alt} onError={() => setFailed(true)} />;
};
const Leistungen = () => (
  <Section id="leistungen">
    <Container>
      <LeistungenTitle>Weitere Leistungen</LeistungenTitle>
      <LeistungenDesc>Neben unserem Schwerpunkt Praxisreinigung übernehmen wir viele weitere Reinigungsaufgaben – individuell nach Bedarf, Objekt und Anlass.</LeistungenDesc>
      <LeistungenGrid as={motion.div}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={{
          hidden: {},
          visible: {
            transition: { staggerChildren: 0.12 }
          }
        }}
      >
        {services.filter((s) => s.slug !== 'praxisreinigung').map((s) => (
          <LeistungCard
            key={s.slug}
            variants={{
              hidden: { opacity: 0, y: 40 },
              visible: { opacity: 1, y: 0 }
            }}
            whileHover={{ y: -6, boxShadow: "0 14px 32px rgba(0, 52, 196, 0.2)" }}
          >
            <CardLink to={`/${s.slug}`}>
              <LeistungImageWrap>
                <LeistungImageFallback name={s.image} alt={s.title} />
              </LeistungImageWrap>
              <CardHeader>
                <LeistungenIconTitle>
                  <s.icon style={{ height: 20, width: 20 }} />
                  {s.title}
                </LeistungenIconTitle>
              </CardHeader>
              <LeistungenContent>
                {s.shortDesc}
                <LeistungSubList>
                  {s.items.map((item, j) => (
                    <LeistungSubItem key={j}>
                      <CheckCircle2 style={{ height: 18, width: 18, color: 'var(--brand)', flexShrink: 0, marginTop: 2 }} />
                      <span>{item}</span>
                    </LeistungSubItem>
                  ))}
                </LeistungSubList>
                <LeistungLink to={`/${s.slug}`}>Mehr erfahren →</LeistungLink>
              </LeistungenContent>
            </CardLink>
          </LeistungCard>
        ))}
      </LeistungenGrid>
    </Container>
  </Section>
);

// --- Vorteile ---
const VorteileSection = styled(Section)`
  background: var(--surface-alt);
`;
const VorteileTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
  color: var(--brand);
  margin-bottom: 2rem;
`;
const VorteileGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
  max-width: 900px;
  margin: 0 auto;
  @media (min-width: 700px) {
    grid-template-columns: 1fr 1fr;
  }
`;
const VorteilItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.1rem 1.3rem;
  color: var(--ink-muted);
  box-shadow: var(--shadow-sm);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-md);
  }
`;
// Was in der Praxisreinigung erst ab einer Paketstufe gilt, nennt die Stufe,
// deckungsgleich mit `praxisPakete` in data/services.tsx.
const vorteile = [
  "Kein Reinigungswagen im Wartezimmer: auf Wunsch komplett außerhalb Ihrer Öffnungszeiten",
  "Ein fester Ansprechpartner statt Hotline und Ticketnummer",
  "Ausschließlich unser eigenes, festes Team — keine Subunternehmer",
  "Diskretion: Wir unterschreiben eine Verschwiegenheitserklärung — in Praxen ab Praxis Plus zusätzlich mit speziell eingewiesenem Personal für sensible Bereiche",
  "Ein Reinigungsplan, der zu Ihrem Objekt passt — in Praxen ab Praxis Plus als individueller Reinigungs- und Desinfektionsplan",
  "Nachweis für die nächste Begehung: Checklisten und Dokumentation auf Wunsch, in Praxen ab Praxis Plus",
  "Etwas übersehen? Wir bessern nach — bei berechtigten Beanstandungen, in Praxen im Paket Praxis Komfort",
  "Rückmeldung auf Ihre Anfrage innerhalb von 24 Stunden, damit Sie nicht hinterhertelefonieren",
  "Kostenlose Besichtigung vor Ort, bevor Sie sich festlegen",
];
const Vorteile = () => (
  <VorteileSection id="vorteile">
    <Container>
      <VorteileTitle>Worum Sie sich nicht mehr kümmern müssen</VorteileTitle>
      <VorteileGrid as={motion.div}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
      >
        {vorteile.map((v, i) => (
          <VorteilItem
            key={i}
            as={motion.div}
            variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
          >
            <CheckCircle2 style={{ height: 22, width: 22, color: 'var(--brand)', flexShrink: 0, marginTop: 2 }} />
            <span>{v}</span>
          </VorteilItem>
        ))}
      </VorteileGrid>
    </Container>
  </VorteileSection>
);

// --- Praxis-Spotlight (Schwerpunkt prominent im Vordergrund) ---
const praxisService = getServiceBySlug('praxisreinigung');

const PraxisSpotlightSection = styled(Section)`
  background: linear-gradient(150deg, #001a63 0%, var(--brand) 45%, #234be6 100%);
  color: #fff;
  overflow: hidden;
`;
const SpotlightHead = styled.div`
  text-align: center;
  max-width: 760px;
  margin: 0 auto 3rem auto;
`;
const SpotlightBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(255,255,255,0.16);
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 999px;
  padding: 0.4em 1.1em;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 1.2rem;
`;
const SpotlightTitle = styled.h2`
  font-size: 2.3rem;
  font-weight: 800;
  line-height: 1.15;
  margin-bottom: 1rem;
  @media (min-width: 700px) {
    font-size: 2.7rem;
  }
`;
const SpotlightLead = styled.p`
  font-size: 1.15rem;
  line-height: 1.7;
  color: #dfe7ff;
`;
const SpotlightGrid = styled.div`
  display: grid;
  gap: 2.5rem;
  align-items: center;
  grid-template-columns: 1fr;
  @media (min-width: 900px) {
    grid-template-columns: 1.05fr 1fr;
  }
`;
const SpotlightImageWrap = styled(motion.div)`
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  aspect-ratio: 4 / 3;
  background: linear-gradient(135deg, #234be6 0%, #6f8df5 100%);
  box-shadow: 0 24px 60px rgba(0,0,0,0.35);
`;
const SpotlightImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
const SpotlightImageResponsive: React.FC<{ name: ImageName; alt: string }> = ({ name, alt }) => {
  const [failed, setFailed] = React.useState(false);
  if (failed) return null;
  return <SpotlightImage {...imageProps(name, SIZES.spotlight)} alt={alt} onError={() => setFailed(true)} />;
};
const SpotlightImageBadge = styled.div`
  position: absolute;
  left: 18px;
  bottom: 18px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255,255,255,0.95);
  color: var(--brand);
  font-weight: 700;
  font-size: 0.95rem;
  padding: 0.55em 1.1em;
  border-radius: 999px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.25);
`;
const SpotlightBenefits = styled.ul`
  list-style: none;
  margin: 0 0 2rem 0;
  padding: 0;
  display: grid;
  gap: 0.8rem;
`;
const SpotlightBenefit = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 1.05rem;
  color: #eef2ff;
`;
const PaketRow = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
  margin: 0 auto 2.5rem auto;
  max-width: 1000px;
  @media (min-width: 800px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;
const PaketCard = styled(motion.div)<{ $recommended?: boolean }>`
  position: relative;
  background: ${p => (p.$recommended ? '#fff' : 'rgba(255,255,255,0.08)')};
  color: ${p => (p.$recommended ? '#1f2a44' : '#fff')};
  border: 1px solid ${p => (p.$recommended ? 'transparent' : 'rgba(255,255,255,0.22)')};
  border-radius: 20px;
  padding: 1.8rem 1.4rem;
  display: flex;
  flex-direction: column;
  box-shadow: ${p => (p.$recommended ? '0 20px 48px rgba(0,0,0,0.30)' : 'none')};
  ${p => p.$recommended && 'transform: translateY(-6px);'}
`;
const PaketRibbon = styled.span`
  position: absolute;
  top: -13px;
  left: 50%;
  transform: translateX(-50%);
  background: #fbbc04;
  color: #1f2a44;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 0.32em 0.9em;
  border-radius: 999px;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
`;
const PaketName = styled.div`
  font-size: 1.25rem;
  font-weight: 800;
  margin-bottom: 0.35rem;
`;
const PaketFor = styled.div<{ $recommended?: boolean }>`
  font-size: 0.92rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  color: ${p => (p.$recommended ? '#5a6683' : '#d5deff')};
`;
const PaketFeatures = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.5rem;
`;
const PaketFeature = styled.li<{ $recommended?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.92rem;
  line-height: 1.4;
  color: ${p => (p.$recommended ? '#3a4662' : '#eef2ff')};
`;
const SpotlightCtaRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  @media (min-width: 600px) {
    flex-direction: row;
  }
`;
const PraxisSpotlight = () => {
  if (!praxisService) return null;
  return (
    <PraxisSpotlightSection id="praxisreinigung">
      <FloatingBlobs />
      <Container style={{ position: 'relative', zIndex: 1 }}>
        <SpotlightHead>
          <SpotlightBadge><Star style={{ height: 15, width: 15 }} />Unser Schwerpunkt</SpotlightBadge>
          <SpotlightTitle>Spezialisierte Praxisreinigung – unsere Kernkompetenz</SpotlightTitle>
          <SpotlightLead>
            Arzt-, Zahnarzt- und Therapiepraxen brauchen mehr als sichtbare Sauberkeit: feste Abläufe, diskreten Umgang mit sensiblen Bereichen und eine Reinigung, die zur Praxisorganisation passt. Genau darauf sind wir spezialisiert.
          </SpotlightLead>
        </SpotlightHead>

        <SpotlightGrid style={{ marginBottom: '3.5rem' }}>
          <SpotlightImageWrap
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <SpotlightImageResponsive
              name={praxisService.image}
              alt="Spezialisierte Praxisreinigung von AP Clean Services"
            />
            <SpotlightImageBadge><Stethoscope style={{ height: 16, width: 16 }} />Nach Praxisschluss</SpotlightImageBadge>
          </SpotlightImageWrap>
          <div>
            <SpotlightBenefits
              as={motion.ul}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.09 } } }}
            >
              {praxisService.benefits.map((b, i) => (
                <SpotlightBenefit
                  key={i}
                  as={motion.li}
                  variants={{ hidden: { opacity: 0, x: -16 }, visible: { opacity: 1, x: 0 } }}
                >
                  <CheckCircle2 style={{ height: 22, width: 22, color: '#8ffcc0', flexShrink: 0, marginTop: 1 }} />
                  <span>{b}</span>
                </SpotlightBenefit>
              ))}
            </SpotlightBenefits>
          </div>
        </SpotlightGrid>

        <PaketRow
          as={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }}
        >
          {praxisService.pakete?.map((p) => (
            <PaketCard
              key={p.name}
              $recommended={p.recommended}
              variants={{ hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } }}
            >
              {p.recommended && <PaketRibbon>Am häufigsten gewählt</PaketRibbon>}
              <PaketName>{p.name}</PaketName>
              <PaketFor $recommended={p.recommended}>{p.for}</PaketFor>
              <PaketFeatures>
                {p.features.map((f, j) => (
                  <PaketFeature key={j} $recommended={p.recommended}>
                    <CheckCircle2 style={{ height: 17, width: 17, color: p.recommended ? 'var(--brand)' : '#8ffcc0', flexShrink: 0, marginTop: 2 }} />
                    <span>{f}</span>
                  </PaketFeature>
                ))}
              </PaketFeatures>
            </PaketCard>
          ))}
        </PaketRow>

        <SpotlightCtaRow>
          <Link to="/praxisreinigung"><Button size="lg" variant="outline">Alle Praxis-Pakete ansehen</Button></Link>
          <WhatsAppButton size="lg" />
        </SpotlightCtaRow>
      </Container>
    </PraxisSpotlightSection>
  );
};

// --- Ablauf ---
const AblaufSection = styled(Section)`
  background: var(--surface-alt);
`;
const AblaufTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
  color: var(--brand);
  margin-bottom: 2.5rem;
`;
const AblaufList = styled.ol`
  list-style: none;
  counter-reset: ablauf;
  margin: 0 auto;
  padding: 0;
  max-width: 760px;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;
const AblaufStep = styled.li`
  counter-increment: ablauf;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.1rem 1.4rem;
  box-shadow: var(--shadow-sm);
  color: var(--ink-muted);
  font-size: 1.05rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-md);
  }
  &:before {
    content: counter(ablauf);
    flex-shrink: 0;
    width: 2.2rem;
    height: 2.2rem;
    border-radius: 50%;
    background: var(--brand);
    color: #fff;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
const ablaufSchritte = [
  "Kostenlose Besichtigung Ihres Objekts",
  "Aufnahme der Räume und gewünschten Reinigungsintervalle",
  "Erstellung eines transparenten Festpreisangebots",
  "Abstimmung von Starttermin, Schlüssel und Ansprechpartner",
  "Beginn der Reinigung, im Paket Praxis Komfort mit Feedback nach der Startphase",
];
const Ablauf = () => (
  <AblaufSection id="ablauf">
    <Container>
      <AblaufTitle>So einfach läuft die Zusammenarbeit ab</AblaufTitle>
      <AblaufList as={motion.ol}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }}
      >
        {ablaufSchritte.map((s, i) => (
          <AblaufStep
            key={i}
            as={motion.li}
            variants={{ hidden: { opacity: 0, x: -20 }, visible: { opacity: 1, x: 0 } }}
          >
            <span style={{ alignSelf: 'center' }}>{s}</span>
          </AblaufStep>
        ))}
      </AblaufList>
    </Container>
  </AblaufSection>
);

// --- Vorher / Nachher ---
const VorherNachherTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
  color: var(--brand);
  margin-bottom: 0.75rem;
`;
const VorherNachherIntro = styled.p`
  max-width: 680px;
  margin: 0 auto 2.75rem auto;
  text-align: center;
  font-size: 1.1rem;
  line-height: 1.7;
  color: var(--ink-muted);
`;
// Bewusst nur eine Karte pro Zeile. Zuvor standen zwei nebeneinander und
// jedes Foto war 231px breit - auf dem Handy sogar nur 154px. Bei der Groesse
// ist von einem Vorher/Nachher-Unterschied nichts zu sehen, die Sektion hat
// ihr Versprechen also nicht eingeloest.
const VorherNachherGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
  max-width: 960px;
  margin: 0 auto;
`;
const VergleichCard = styled(motion.div)`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
  }
`;
const VergleichImages = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  @media (min-width: 560px) {
    grid-template-columns: 1fr 1fr;
  }
`;
const VergleichImageWrap = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  /* Wie bei den uebrigen Bild-Wrappern: faellt ein Foto aus, bleibt der
     Marken-Verlauf stehen statt einer weissen Flaeche mit Label darauf. */
  background: linear-gradient(135deg, var(--brand) 0%, var(--brand-600) 55%, var(--brand-light) 100%);
`;
const VergleichImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;
const VergleichLabel = styled.span<{ $after?: boolean }>`
  position: absolute;
  top: 0.7rem;
  ${(p) => (p.$after ? 'right: 0.7rem;' : 'left: 0.7rem;')}
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 0.28rem 0.7rem;
  border-radius: 999px;
  color: #fff;
  background: ${(p) => (p.$after ? 'var(--brand)' : 'rgba(20, 30, 50, 0.72)')};
  backdrop-filter: blur(4px);
`;
const VergleichCaption = styled.div`
  padding: 1rem 1.25rem 1.15rem;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--ink);
  text-align: center;
`;
type Vergleich = {
  title: string;
  vorher: ImageName;
  nachher: ImageName;
};
const vergleiche: Vergleich[] = [
  { title: 'Bodenreinigung', vorher: 'boden-vorher', nachher: 'boden-nachher' },
  { title: 'Sanitärreinigung', vorher: 'sanitaer-vorher', nachher: 'sanitaer-nachher' },
];
const VergleichImgResponsive: React.FC<{ name: ImageName; alt: string }> = ({ name, alt }) => {
  const [failed, setFailed] = React.useState(false);
  if (failed) return null;
  return <VergleichImg {...imageProps(name, SIZES.comparison)} alt={alt} onError={() => setFailed(true)} />;
};
const VorherNachher = () => (
  <Section id="vorher-nachher">
    <Container>
      <VorherNachherTitle>Ergebnisse, die man sieht</VorherNachherTitle>
      <VorherNachherIntro>
        Ein direkter Vergleich sagt mehr als viele Worte: So sehen unsere Flächen vor und nach der Reinigung aus.
      </VorherNachherIntro>
      <VorherNachherGrid
        as={motion.div}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.15 } } }}
      >
        {vergleiche.map((v, i) => (
          <VergleichCard
            key={i}
            variants={{ hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } }}
          >
            <VergleichImages>
              <VergleichImageWrap>
                <VergleichImgResponsive name={v.vorher} alt={`${v.title} vorher`} />
                <VergleichLabel>Vorher</VergleichLabel>
              </VergleichImageWrap>
              <VergleichImageWrap>
                <VergleichImgResponsive name={v.nachher} alt={`${v.title} nachher`} />
                <VergleichLabel $after>Nachher</VergleichLabel>
              </VergleichImageWrap>
            </VergleichImages>
            <VergleichCaption>{v.title}</VergleichCaption>
          </VergleichCard>
        ))}
      </VorherNachherGrid>
    </Container>
  </Section>
);

// --- Vertrauen ---
const VertrauenTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
  color: var(--brand);
  margin-bottom: 1.5rem;
`;
const VertrauenText = styled.p`
  max-width: 760px;
  margin: 0 auto 1.2rem auto;
  text-align: center;
  font-size: 1.1rem;
  line-height: 1.7;
  color: var(--ink-muted);
`;
const Vertrauen = () => (
  <Section id="vertrauen">
    <Container>
      <VertrauenTitle>Warum Kunden mit AP Clean Services arbeiten</VertrauenTitle>
      <VertrauenText>
        Wir legen Wert auf zuverlässige Ausführung, diskreten Umgang mit sensiblen Bereichen und klare Kommunikation. Unsere Reinigung erfolgt nach vereinbartem Plan und wird auf das jeweilige Objekt abgestimmt. So wissen Sie genau, welche Leistungen wann ausgeführt werden.
      </VertrauenText>
      <VertrauenText>
        AP Clean Services ist Ihr spezialisierter Ansprechpartner für Praxisreinigung sowie für Büro-, Treppenhaus-, Glas- und Sonder- & Anlassreinigung in Bielefeld, Gütersloh und Umgebung.
      </VertrauenText>
    </Container>
  </Section>
);

// --- Bewertungen ---
const BewertungenSection = styled(Section)`
  background: var(--surface-alt);
`;
const BewertungenTitle = styled.h2`
  text-align: center;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: var(--brand);
`;
const BewertungenGrid = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: 1fr;
  max-width: 900px;
  margin: 0 auto;
  @media (min-width: 700px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;
const RatingSummary = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2.5rem;
`;
const RatingScore = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;
const RatingBig = styled.span`
  font-size: 2.4rem;
  font-weight: 800;
  color: var(--brand);
  line-height: 1;
`;
const GoogleMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: #5f6368;
  font-size: 0.98rem;
`;
const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  margin-bottom: 0.75rem;
`;
const Avatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  color: #fff;
  font-weight: 700;
  font-size: 1.05rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.10);
`;
const NameCol = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;
const BewertungName = styled.div`
  font-weight: 600;
  color: #1f2a44;
  line-height: 1.2;
`;
const LocalGuide = styled.span`
  font-size: 0.8rem;
  color: #5f6368;
`;
const StarRow = styled.div`
  color: #fbbc04;
  font-size: 1.35rem;
  letter-spacing: 2px;
  line-height: 1;
`;
const BewertungText = styled.div`
  color: #3a4662;
  font-size: 1.02rem;
  line-height: 1.6;
  margin-top: 0.75rem;
`;
const MoreToggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-left: 0.35rem;
  color: var(--brand);
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;
const ReviewCtaRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2.5rem;
`;
const MotionBewertungCard = styled(motion.div)`
  background: #fff;
  border: 1px solid #e6e9f2;
  box-shadow: 0 4px 24px rgba(0,52,196,0.08);
  border-radius: 22px;
  padding: 1.75rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  position: relative;
  transition: box-shadow 0.2s, transform 0.2s;
  will-change: transform;
`;

const GOOGLE_REVIEWS_URL =
  'https://www.google.com/maps/place/AP+Clean+Services/@51.9261252,8.4119001,17z/data=!3m1!4b1!4m6!3m5!1s0x18fe9704baeb83:0x68211c1d3c3c9ae5!8m2!3d51.9261252!4d8.4119001!16s%2Fg%2F11nq2cq190';
const AVATAR_COLORS = ['#1a73e8', '#d93025', '#188038', '#e37400', '#9334e6', '#0b8043'];
const REVIEW_CLAMP = 180;

const GoogleG: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
  </svg>
);
const Stars: React.FC<{ rating?: number }> = ({ rating = 5 }) => (
  <StarRow aria-label={`${rating} von 5 Sternen`}>
    {'★★★★★'.slice(0, rating)}
    <span style={{ color: '#dadce0' }}>{'★★★★★'.slice(rating)}</span>
  </StarRow>
);

type Bewertung = { name: string; rating: number; text: string; localGuide?: boolean };
const bewertungen: Bewertung[] = [
  {
    name: 'Shankeetha Jeyapragash',
    rating: 5,
    text: 'Sehr gute und zuverlässige Fensterreinigung. Die Absprache lief unkompliziert, der Termin wurde pünktlich eingehalten und das Ergebnis war wirklich sauber. Auch Rahmen und Fensterbänke wurden ordentlich mitgemacht. Ich bin sehr zufrieden und würde AP Clean Services wieder beauftragen.',
  },
  {
    name: 'Jey Jey',
    rating: 5,
    text: 'Ich bin wirklich sehr zufrieden mit AP Clean Services und kann das Unternehmen mit bestem Gewissen weiterempfehlen. Letzte Woche wurde eines meiner Gebäude von dem Team gereinigt. Die Arbeit wurde sehr gründlich, sauber und professionell erledigt. Das Preis-Leistungs-Verhältnis ist ebenfalls top. Besonders gefallen haben mir die Pünktlichkeit, die freundlichen und höflichen Mitarbeiter sowie die unkomplizierte Kommunikation. Egal wann ich Kontakt aufgenommen habe, ich habe immer schnell eine Rückmeldung erhalten. Nach dieser positiven Erfahrung habe ich mich entschieden, AP Clean Services auch für die regelmäßige Reinigung unserer Büroräume zu beauftragen. Vielen Dank an das gesamte Team für die tolle Arbeit. Ich bin wirklich sehr zufrieden und freue mich auf die weitere Zusammenarbeit. Ich wünsche euch weiterhin viel Erfolg – macht weiter so!',
  },
  {
    name: 'Christian',
    rating: 5,
    localGuide: true,
    text: 'Ich bin wirklich super zufrieden mit AP Clean Services. Die Reinigung wurde sehr gründlich durchgeführt und alles sah danach top aus. Das Team war freundlich, pünktlich und hat sauber gearbeitet. Man merkt, dass hier Wert auf Qualität gelegt wird. Kann ich mit gutem Gewissen weiterempfehlen. Vielen Dank für den tollen Service.',
  },
  {
    name: 'Nils Kramme',
    rating: 5,
    text: 'Absolut empfehlenswert! Wir haben AP Clean Services gebucht und sind restlos begeistert. Das Team arbeitet extrem gründlich, pünktlich und professionell. Von der ersten Kontaktaufnahme bis zum glänzenden Endergebnis lief alles reibungslos. Vielen Dank für den tollen Service – wir buchen euch definitiv wieder!',
  },
  {
    name: 'Endrit Ajeti',
    rating: 5,
    text: 'Unglaublich kompetent und zuverlässig.',
  },
  {
    name: 'Ismail Günes',
    rating: 5,
    localGuide: true,
    text: 'Vom Erstkontakt zur Auftragsabnahme war alles top! Angebot kam schnell, der Preis war mehr als fair und das gesamte Objekt ist jetzt wieder so sauber wie noch nie. Man merkt, dass hier noch mit Sorgfalt gearbeitet wird. VIELEN DANK!! Wir werden euch definitiv wieder beauftragen.',
  },
];
const getInitials = (name: string) => name.split(' ').map((n: string) => n[0]).join('').toUpperCase();

const ReviewCard: React.FC<{ b: Bewertung; index: number }> = ({ b, index }) => {
  const [expanded, setExpanded] = React.useState(false);
  const isLong = b.text.length > REVIEW_CLAMP;
  const shown = expanded || !isLong ? b.text : b.text.slice(0, REVIEW_CLAMP).trimEnd() + '…';
  return (
    <MotionBewertungCard
      variants={{ hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } }}
      whileHover={{ y: -6, boxShadow: '0 12px 36px rgba(0,52,196,0.18)' }}
    >
      <CardTop>
        <Avatar style={{ background: AVATAR_COLORS[index % AVATAR_COLORS.length] }}>{getInitials(b.name)}</Avatar>
        <NameCol>
          <BewertungName>{b.name}</BewertungName>
          {b.localGuide && <LocalGuide>Local Guide</LocalGuide>}
        </NameCol>
        <GoogleG size={22} />
      </CardTop>
      <Stars rating={b.rating} />
      <BewertungText>
        {shown}
        {isLong && (
          <MoreToggle type="button" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
          </MoreToggle>
        )}
      </BewertungText>
    </MotionBewertungCard>
  );
};

const Bewertungen = () => (
  <BewertungenSection id="bewertungen">
    <Container>
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.15 } } }}
      >
        <BewertungenTitle>Das sagen unsere Kunden</BewertungenTitle>
        <RatingSummary>
          <RatingScore>
            <RatingBig>5,0</RatingBig>
            <Stars rating={5} />
          </RatingScore>
          <GoogleMeta>
            <GoogleG size={18} /> Google-Rezensionen
          </GoogleMeta>
        </RatingSummary>
        <BewertungenGrid>
          {bewertungen.map((b, i) => (
            <ReviewCard key={i} b={b} index={i} />
          ))}
        </BewertungenGrid>
        <ReviewCtaRow>
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <Button variant="outline">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                <GoogleG size={18} /> Alle Bewertungen auf Google ansehen
              </span>
            </Button>
          </a>
        </ReviewCtaRow>
      </motion.div>
    </Container>
  </BewertungenSection>
);

const Home: React.FC = () => (
  <>
    <Seo
      title="Praxisreinigung & Gebäudereinigung Bielefeld & Gütersloh | AP Clean Services"
      description="AP Clean Services: spezialisierte Praxisreinigung sowie Büro-, Treppenhaus-, Glas- und Sonder- & Anlassreinigung in Bielefeld, Gütersloh und Umgebung. Kostenlose Besichtigung anfragen."
      path="/"
      image={socialImagePath('praxisreinigung')}
    />
    <AnimatedSection><Hero /></AnimatedSection>
    <SectionDivider color="#f6f8fc" />

    {/* 1. Problem: der Schmerz, bevor irgendeine Leistung genannt wird */}
    <AnimatedSection><Problem /></AnimatedSection>

    {/* 2. Loesung: erst der Schwerpunkt, dann was sich fuer den Kunden aendert */}
    <AnimatedSection variant="scale"><PraxisSpotlight /></AnimatedSection>
    <AnimatedSection variant="right"><Vorteile /></AnimatedSection>
    <SectionDivider color="#f6f8fc" flip />

    {/* 3. Beweis: sichtbares Ergebnis, Einordnung, fremde Stimmen */}
    <AnimatedSection variant="scale"><VorherNachher /></AnimatedSection>
    <SectionDivider color="#f6f8fc" />
    <AnimatedSection variant="left"><Vertrauen /></AnimatedSection>
    <SectionDivider color="#f6f8fc" />
    <AnimatedSection variant="scale"><Bewertungen /></AnimatedSection>
    <SectionDivider color="#f6f8fc" flip />

    {/* 4. Breite des Angebots: erst jetzt, wenn der Bedarf geklaert ist */}
    <AnimatedSection variant="right"><Leistungen /></AnimatedSection>
    <SectionDivider color="#f6f8fc" />

    {/* 5. Zusammenarbeit, Einsatzgebiet, Preis, Kontakt */}
    <AnimatedSection variant="left"><Ablauf /></AnimatedSection>
    <SectionDivider color="#f6f8fc" flip />
    <AnimatedSection variant="right">
      <Section>
        <Container>
          <StandorteSection />
        </Container>
      </Section>
    </AnimatedSection>
    <SectionDivider color="#f6f8fc" flip />
    <AnimatedSection variant="scale"><Preisrechner /></AnimatedSection>
    <SectionDivider color="#e6edfa" flip />
    <AnimatedSection><Kontakt /></AnimatedSection>
  </>
);

export default Home;
